import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import ProposalState from "../constants/ProposalState";
import { fetchClientGigs, fetchClientGigsWithState } from "../firebase/gig";
import { GigsAndProposalData } from "../types/types";
import { fetchGetJSON } from "../utils/api-helpers";

export interface GigAndProposalDataState {
  data: Record<
    ProposalState,
    { count: number; data: GigsAndProposalData[] | [] }
  >;
  likedData: Record<
    ProposalState,
    { count: number; data: GigsAndProposalData[] | [] }
  >;
  isLoading: boolean;
  isSuccess: boolean;
  errorMessage: string;
}

const initialState: GigAndProposalDataState = {
  data: {
    [ProposalState.DRAFT]: { count: 0, data: [] },
    [ProposalState.NEW_GIG]: { count: 0, data: [] },
    [ProposalState.ACCEPTED_BOOKED]: { count: 0, data: [] },
    [ProposalState.AWAITING_PAYMENT]: { count: 0, data: [] },
    [ProposalState.CANCELED_BID]: { count: 0, data: [] },
    [ProposalState.CANCELED_GIG]: { count: 0, data: [] },
    [ProposalState.COMPLETED]: { count: 0, data: [] },
    [ProposalState.DECLINED_BID]: { count: 0, data: [] },
    [ProposalState.DEPOSIT_PAID]: { count: 0, data: [] },
    [ProposalState.FINISHED_AND_PAID]: { count: 0, data: [] },
    [ProposalState.INVOICE_CHANGE]: { count: 0, data: [] },
    [ProposalState.OPEN_BID]: { count: 0, data: [] },
    [ProposalState.PAID]: { count: 0, data: [] },
    [ProposalState.PENDING]: { count: 0, data: [] },
    [ProposalState.NONE]: { count: 0, data: [] },
    [ProposalState.LIKED]: { count: 0, data: [] },
  },
  likedData: {
    [ProposalState.DRAFT]: { count: 0, data: [] },
    [ProposalState.NEW_GIG]: { count: 0, data: [] },
    [ProposalState.ACCEPTED_BOOKED]: { count: 0, data: [] },
    [ProposalState.AWAITING_PAYMENT]: { count: 0, data: [] },
    [ProposalState.CANCELED_BID]: { count: 0, data: [] },
    [ProposalState.CANCELED_GIG]: { count: 0, data: [] },
    [ProposalState.COMPLETED]: { count: 0, data: [] },
    [ProposalState.DECLINED_BID]: { count: 0, data: [] },
    [ProposalState.DEPOSIT_PAID]: { count: 0, data: [] },
    [ProposalState.FINISHED_AND_PAID]: { count: 0, data: [] },
    [ProposalState.INVOICE_CHANGE]: { count: 0, data: [] },
    [ProposalState.OPEN_BID]: { count: 0, data: [] },
    [ProposalState.PAID]: { count: 0, data: [] },
    [ProposalState.PENDING]: { count: 0, data: [] },
    [ProposalState.NONE]: { count: 0, data: [] },
    [ProposalState.LIKED]: { count: 0, data: [] },
  },
  isLoading: false,
  isSuccess: false,
  errorMessage: "",
};

export const gigsAndProposalsSlice = createSlice({
  name: "gigandproposal",
  initialState,
  reducers: {
    addData: (
      state,
      action: PayloadAction<{
        status: ProposalState;
        data: GigsAndProposalData[];
      } | null>
    ) => {
      const status = action.payload?.status;
      if (status && state.data) {
        const prevState = { ...state };

        state.data[status] = {
          ...prevState.data[status],
          data: action.payload?.data || [],
        };
      }
    },
    addLikedData: (
      state,
      action: PayloadAction<{
        status: ProposalState;
        data: GigsAndProposalData[];
      } | null>
    ) => {
      const status = action.payload?.status;
      if (status && state.likedData) {
        const prevState = { ...state };
        state.likedData[status] = {
          ...prevState.likedData[status],
          data: action.payload?.data || [],
        };
      }
    },
    addDataCount: (
      state,
      action: PayloadAction<{
        status: ProposalState;
        count: number;
      } | null>
    ) => {
      const status = action.payload?.status;
      if (status && state.data) {
        const prevState = { ...state };
        state.data[status] = {
          ...prevState.data[status],
          count: action.payload?.count ?? 0,
        };
      }
    },
    addLikedCount: (
      state,
      action: PayloadAction<{
        status: ProposalState;
        count: number;
      } | null>
    ) => {
      const status = action.payload?.status;
      if (status && state.likedData) {
        const prevState = { ...state };
        state.likedData[status] = {
          ...prevState.likedData[status],
          count: action.payload?.count ?? 0,
        };
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    resetGigAndProposalState: () => initialState,
  },
});

export const {
  addData,
  setLoading,
  addLikedData,
  addLikedCount,
  addDataCount,
  resetGigAndProposalState,
} = gigsAndProposalsSlice.actions;

export default gigsAndProposalsSlice.reducer;
